import type {
  InfiniteData,
  InvalidateOptions,
  QueryClient
} from '@tanstack/react-query'

import type {
  NotificationItem,
  NotificationListResponse
} from '../api/notificationsApi'
import { notificationsQueryKeys } from '../notifications.constants'

export type FeedData = InfiniteData<NotificationListResponse>

/**
 * The rows of every loaded page, first copy wins. The feed shifts under the
 * cursor as notifications arrive and are read, so a row can come back on the
 * next page.
 */
export function feedItems(data: FeedData | undefined): NotificationItem[] {
  const seen = new Set<string>()
  const items: NotificationItem[] = []

  for (const page of data?.pages ?? []) {
    for (const item of page.notifications ?? []) {
      if (!seen.has(item.id)) {
        seen.add(item.id)
        items.push(item)
      }
    }
  }

  return items
}

/** Takes read rows out of the cached feed; the feed holds unread rows only. */
export function removeFromFeed(
  data: FeedData | undefined,
  ids: string[]
): { data: FeedData | undefined; removed: number } {
  if (!data) {
    return { data, removed: 0 }
  }

  const drop = new Set(ids)
  const removedIds = new Set<string>()

  const pages = data.pages.map(page => {
    const notifications = (page.notifications ?? []).filter(item => {
      if (drop.has(item.id)) {
        removedIds.add(item.id)
        return false
      }
      return true
    })
    return { ...page, notifications }
  })

  const removed = removedIds.size
  if (removed === 0) {
    return { data, removed }
  }

  return {
    data: {
      ...data,
      pages: pages.map(page => ({
        ...page,
        total: Math.max(0, (page.total ?? 0) - removed)
      }))
    },
    removed
  }
}

/**
 * The cached feed after "clear all": every notification is read, the pages
 * not loaded yet included, so no row is left and there is no next page.
 */
export function clearFeed(data: FeedData | undefined): FeedData | undefined {
  if (!data) {
    return data
  }

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      notifications: [],
      total: 0,
      next_cursor: ''
    }))
  }
}

/** How long this tab waits for the echo of its own read. */
const OWN_READ_TTL_MS = 60_000

const ownReads = new Map<string, number>()

/** Remembers ids this tab has just marked read. */
export function recordOwnReads(ids: string[], now: number = Date.now()) {
  for (const id of ids) {
    ownReads.set(id, now)
  }
}

/**
 * The ids of a `read` event that this tab did not read itself. Each own read
 * is swallowed once: the service sends one echo per read.
 */
export function takeForeignReads(
  ids: string[],
  now: number = Date.now()
): string[] {
  for (const [id, at] of ownReads) {
    if (now - at > OWN_READ_TTL_MS) {
      ownReads.delete(id)
    }
  }

  return ids.filter(id => !ownReads.delete(id))
}

export function forgetOwnReads(ids?: string[]) {
  if (!ids) {
    ownReads.clear()
    return
  }
  for (const id of ids) {
    ownReads.delete(id)
  }
}

/** Refetches what a new or read notification changes: the feed and the badge. */
export function invalidateFeed(
  queryClient: QueryClient,
  userId: string,
  options: InvalidateOptions = {}
) {
  void queryClient.invalidateQueries(
    { queryKey: notificationsQueryKeys.list(userId) },
    options
  )
  void queryClient.invalidateQueries(
    { queryKey: notificationsQueryKeys.unreadCount(userId) },
    options
  )
}
